import React, { useState, useEffect, useRef, useLayoutEffect } from "react";
import { useAuth } from "../../App";
import "./home.css";
import Lightbox from '../common/Lightbox';
import { useNavigate } from 'react-router-dom';
import { formatDateVN } from '../../utils/formatDate';
import picUpload from "../../upload/attachment/pic-upload.png";
import vidUpload from "../../upload/attachment/vid-upload.png";

// get a human-friendly display name for posts/users
function displayNameOf(item) {
  if (!item) return '';
  // If caller passed a plain string (username), return it
  if (typeof item === 'string') return item;
  // If `item` is a post-like object, prefer top-level author_* fields
  const tryKeys = ['author_display', 'author_full_name', 'author_fullname', 'authorFullName', 'author_name', 'authorName', 'full_name', 'fullName', 'name', 'displayName', 'username'];
  // If item has an `author` property, inspect it first
  if (item.author) {
    const a = item.author;
    if (typeof a === 'string') {
      // check for top-level alternate name fields on the post
      for (const k of tryKeys) if (item[k]) return item[k];
      return a;
    }
    if (typeof a === 'object') {
      for (const k of ['full_name', 'fullName', 'name', 'displayName', 'username']) if (a[k]) return a[k];
    }
  }
  // fall back to any of the known keys on the object itself
  for (const k of tryKeys) if (item[k]) return item[k];
  // as a last resort stringify the author or return empty
  if (item.author && typeof item.author === 'string') return item.author;
  return '';
}

// client-side max video attachment size (MB). Keep in sync with server's limit.
const MAX_ATTACHMENT_MB = 150;
const MAX_ATTACHMENT_BYTES = MAX_ATTACHMENT_MB * 1024 * 1024;

export default function Home() {
  const { user } = useAuth();
  const storageKey = 'posts_v1';
  const apiUrl = (process.env.REACT_APP_API_URL);
  const [posts, setPosts] = useState(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      return raw ? JSON.parse(raw) : [];
    } catch (e) { return []; }
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [modalInitialFiles, setModalInitialFiles] = useState([]);
  const [pendingVisible, setPendingVisible] = useState(false);
  const [pendingPosts, setPendingPosts] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [lightboxItems, setLightboxItems] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [previewPost, setPreviewPost] = useState(null);
  const [homeroomTeacherUsername, setHomeroomTeacherUsername] = useState(null);
  const [teacherClassMap, setTeacherClassMap] = useState({});
  const [isFetchingPosts, setIsFetchingPosts] = useState(false);
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth <= 520 : false);

  // persist local copy
  useEffect(() => { try { localStorage.setItem(storageKey, JSON.stringify(posts)); } catch (e) { } }, [posts]);

  useEffect(() => {
    function onResize() { setIsMobile(window.innerWidth <= 520); }
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // fetch from API if configured
  // fetch posts: run when apiUrl or user changes (avoid using `posts` in deps)
  useEffect(() => {
    let mounted = true;
    const controller = new AbortController();
    async function fetchPosts() {
      if (!apiUrl) return; // no API configured
      const token = localStorage.getItem('token');
      if (!token) return; // not authenticated for API
      if (isFetchingPosts) return; // avoid concurrent fetches
      setIsFetchingPosts(true);
      setLoading(true); setError(null);
      try {
        const res = await fetch(`${apiUrl}/api/posts`, { headers: { Authorization: `Bearer ${token}` }, signal: controller.signal });
        if (!res.ok) { throw new Error(`HTTP ${res.status}`); }
        const data = await res.json();
        if (mounted && data && Array.isArray(data.posts)) {
          // normalize posts shape
          const normalized = data.posts.map(p => ({
            id: p.id,
            author: p.author,
            author_first_name: p.author_first_name || (p.author && p.author.first_name) || null,
            author_last_name: p.author_last_name || (p.author && p.author.last_name) || null,
            author_display: p.author_display || p.author_full_name || p.author_fullname || p.full_name || (p.author && typeof p.author === 'object' && (p.author.full_name || p.author.fullName || p.author.name)) || null,
            authorRole: p.authorRole || p.role || '',
            authorSchoolId: p.authorSchoolId || p.author_school_id || null,
            title: p.title,
            content: p.content,
            createdAt: p.createdAt,
            likes: p.likes || [],
            comments: p.comments || [],
            approved: (typeof p.approved === 'boolean') ? p.approved : (p.approved === 1 || p.approved === '1'),
            attachments: (Array.isArray(p.attachments) ? p.attachments : (p.attachments ? (typeof p.attachments === 'string' ? JSON.parse(p.attachments) : p.attachments) : [])).map(a => normalizeAttachmentUrl(a))
          }));
          setPosts(normalized);
          try { console.debug('[Home] fetched posts', { count: normalized.length, authors: normalized.slice(0, 10).map(x => x.author) }); } catch (e) { }
        }
      } catch (e) {
        if (e.name === 'AbortError') { /* aborted */ }
        else {
          console.error('Fetch posts failed', e);
          setError('Không thể tải bài viết từ server, dùng chế độ offline.');
        }
      } finally { setLoading(false); setIsFetchingPosts(false); }
    }
    fetchPosts();

    // also fetch pending count so badge is visible immediately
    (async () => { try { const token = localStorage.getItem('token'); if (!apiUrl || !token) return; const r = await fetch(`${apiUrl}/api/posts/pending`, { headers: { Authorization: `Bearer ${token}` } }); if (r.ok) { const d = await r.json(); if (d && Array.isArray(d.posts)) setPendingCount(d.posts.length); } } catch (e) { } })();

    return () => { mounted = false; controller.abort(); };
  }, [apiUrl, user && user.username]);

  // homeroom teacher lookup for Students — separate effect so it doesn't re-run with posts
  useEffect(() => {
    let mounted = true;
    async function resolveHomeroom() {
      try {
        if (!apiUrl) return setHomeroomTeacherUsername(null);
        if (!user || user.role !== 'Student' || !user.class_id) { setHomeroomTeacherUsername(null); return; }
        const token = localStorage.getItem('token'); if (!token) return;
        const r = await fetch(`${apiUrl}/api/classes`, { headers: { Authorization: `Bearer ${token}` } });
        if (!r.ok) return;
        const jd = await r.json(); const classes = jd.classes || [];
        const cls = classes.find(c => String(c.id) === String(user.class_id));
        const teacher = cls && cls.teacher_username ? cls.teacher_username : null;
        if (mounted) setHomeroomTeacherUsername(teacher);
        try { console.debug('[Home] resolved homeroom teacher for student', { username: user && user.username, class_id: user && user.class_id, teacher }); } catch (e) { }
      } catch (e) { console.error('Failed to fetch class info for student', e); setHomeroomTeacherUsername(null); }
    }
    resolveHomeroom();
    return () => { mounted = false; };
  }, [apiUrl, user && user.role, user && user.class_id]);

  // For Mods: fetch classes once and build a map teacher_username -> class name(s)
  useEffect(() => {
    let mounted = true;
    async function loadTeacherClasses() {
      try {
        if (!apiUrl) return setTeacherClassMap({});
        if (!user || user.role !== 'Mod') return setTeacherClassMap({});
        const token = localStorage.getItem('token'); if (!token) return;
        const r = await fetch(`${apiUrl}/api/classes`, { headers: { Authorization: `Bearer ${token}` } });
        if (!r.ok) return;
        const jd = await r.json(); const classes = jd.classes || [];
        // build map: teacher_username -> array of class names (join with ', ' if multiple)
        const map = {};
        (classes || []).forEach(c => {
          const tu = c.teacher_username || c.teacher || null;
          const cname = c.name || c.class_name || null;
          if (!tu || !cname) return;
          if (!map[tu]) map[tu] = [];
          if (!map[tu].includes(cname)) map[tu].push(cname);
        });
        const flat = {};
        Object.keys(map).forEach(k => { flat[k] = map[k].join(', '); });
        if (mounted) setTeacherClassMap(flat);
      } catch (e) { console.error('Failed to load classes for Mod', e); if (mounted) setTeacherClassMap({}); }
    }
    loadTeacherClasses();
    return () => mounted = false;
  }, [apiUrl, user && user.role]);

  // If API is used, server already filters by role. When in offline/local mode we simply show cached posts.
  const visiblePosts = (() => {
    if (user && user.role === 'Student') {
      // Students should only see posts from their homeroom teacher
      if (homeroomTeacherUsername) return posts.filter(p => String(p.author) === String(homeroomTeacherUsername));
      return [];
    }
    if (apiUrl && localStorage.getItem('token')) return posts; // server filtered
    return posts; // offline: show cached posts as-is
  })();

  const navigate = useNavigate();

  // get a human-friendly display name for posts/users
  function displayNameOf(item) {
    if (!item) return '';
    // If caller passed a plain string (username), return it
    if (typeof item === 'string') return item;
    // If `item` is a post-like object, prefer top-level author_* fields
    const tryKeys = ['author_full_name', 'author_fullname', 'authorFullName', 'author_name', 'authorName', 'full_name', 'fullName', 'name', 'displayName', 'username'];
    // If item has an `author` property, inspect it first
    if (item.author) {
      const a = item.author;
      if (typeof a === 'string') {
        // check for top-level alternate name fields on the post
        for (const k of tryKeys) if (item[k]) return item[k];
        return a;
      }
      if (typeof a === 'object') {
        for (const k of ['full_name', 'fullName', 'name', 'displayName', 'username']) if (a[k]) return a[k];
      }
    }
    // fall back to any of the known keys on the object itself
    for (const k of tryKeys) if (item[k]) return item[k];
    // as a last resort stringify the author or return empty
    if (item.author && typeof item.author === 'string') return item.author;
    return '';
  }

  // precompute visible + non-empty posts to simplify JSX rendering
  const filteredVisiblePosts = visiblePosts.filter(p => {
    const hasTitle = p.title && String(p.title).trim().length > 0;
    const hasContent = p.content && String(p.content).trim().length > 0;
    const hasAttachments = p.attachments && Array.isArray(p.attachments) && p.attachments.length > 0;
    return hasTitle || hasContent || hasAttachments;
  });

  // helper to update local state (merge single post update)
  function updatePostInState(id, updater) {
    setPosts(prev => prev.map(p => p.id === id ? updater(p) : p));
  }

  async function toggleLike(id) {
    const token = localStorage.getItem('token');
    if (apiUrl && token) {
      try {
        // determine current like state from local posts snapshot
        const cur = posts.find(p => String(p.id) === String(id));
        const hadLiked = cur ? (cur.likes || []).includes(user.username) : false;

        const res = await fetch(`${apiUrl}/api/posts/${id}/like`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) throw new Error('like failed');
        const data = await res.json();

        // update state (no toasts inside updater)
        updatePostInState(id, p => {
          const has = (p.likes || []).includes(user.username);
          if (data.liked) {
            if (!has) return { ...p, likes: [...(p.likes || []), user.username] };
            return p;
          } else {
            return { ...p, likes: (p.likes || []).filter(u => u !== user.username) };
          }
        });

        // show a single toast based on server response and previous local state
        if (window.showToast) {
          if (data.liked && !hadLiked) window.showToast('Liked', 'success');
          else if (!data.liked && hadLiked) window.showToast('Unliked', 'error');
        }
      } catch (e) {
        console.error(e); // fallback local
        setPosts(prev => prev.map(p => p.id === id ? { ...p, likes: (p.likes || []).includes(user.username) ? (p.likes || []).filter(u => u !== user.username) : [...(p.likes || []), user.username] } : p));
        if (window.showToast) window.showToast('Like action failed, updated locally', 'warning');
      }
    } else {
      // offline/local mode
      setPosts(prev => prev.map(p => p.id === id ? { ...p, likes: (p.likes || []).includes(user.username) ? (p.likes || []).filter(u => u !== user.username) : [...(p.likes || []), user.username] } : p));
    }
  }

  // Fetch a single post and ensure it is present in the main posts list
  async function fetchPostById(id) {
    if (!apiUrl) return null;
    const token = localStorage.getItem('token');
    if (!token) return null;
    try {
      const res = await fetch(`${apiUrl}/api/posts/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('fetch post failed');
      const d = await res.json();
      const p = d && (d.post || d) ? (d.post || d) : null;
      if (!p || !p.id) return null;
      const normalized = {
        id: p.id,
        author: p.author,
        author_first_name: p.author_first_name || (p.author && p.author.first_name) || null,
        author_last_name: p.author_last_name || (p.author && p.author.last_name) || null,
        author_display: p.author_display || p.author_full_name || p.author_fullname || p.full_name || (p.author && typeof p.author === 'object' && (p.author.full_name || p.author.fullName || p.author.name)) || null,
        authorRole: p.authorRole || p.role || '',
        authorSchoolId: p.authorSchoolId || p.author_school_id || null,
        title: p.title,
        content: p.content,
        createdAt: p.createdAt,
        likes: p.likes || [],
        comments: p.comments || [],
        approved: (typeof p.approved === 'boolean') ? p.approved : (p.approved === 1 || p.approved === '1'),
        attachments: (Array.isArray(p.attachments) ? p.attachments : (p.attachments ? (typeof p.attachments === 'string' ? JSON.parse(p.attachments) : p.attachments) : [])).map(a => normalizeAttachmentUrl(a))
      };
      setPosts(prev => {
        const exists = prev.some(x => x.id === normalized.id);
        if (exists) return prev.map(x => x.id === normalized.id ? normalized : x);
        return [normalized, ...prev];
      });
      return normalized;
    } catch (e) { console.error('fetchPostById failed', e); return null; }
  }

  async function addComment(id, text) {
    const token = localStorage.getItem('token');
    if (apiUrl && token) {
      try {
        const res = await fetch(`${apiUrl}/api/posts/${id}/comment`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ text }) });
        if (!res.ok) throw new Error('comment failed');
        const data = await res.json();
        if (data && data.comment) {
          // merge server comment with local user info when it belongs to current user
          const serverComment = { user: data.comment.user, text: data.comment.text, first_name: data.comment.first_name, last_name: data.comment.last_name, avt: data.comment.avt };
          // ensure we have a created timestamp so the UI can display time immediately
          try {
            const nowIso = (new Date()).toISOString();
            serverComment.createdAt = data.comment.createdAt || data.comment.created_at || nowIso;
            serverComment.created_at = serverComment.createdAt;
          } catch (e) { /* ignore */ }
          if (user && serverComment.user && String(serverComment.user) === String(user.username)) {
            serverComment.first_name = serverComment.first_name || (user.first_name || user.firstName) || undefined;
            serverComment.last_name = serverComment.last_name || (user.last_name || user.lastName) || undefined;
            serverComment.avt = serverComment.avt || (user.avt || user.avatar) || undefined;
          }
          updatePostInState(id, p => ({ ...p, comments: [...(p.comments || []), serverComment] }));
          if (window.showToast) window.showToast('Comment sent', 'success');
        }
      } catch (e) {
        console.error(e); // fallback local
        // optimistic local comment: include user's name/avatar so UI displays immediately
        const nowIso = (new Date()).toISOString();
        const newComment = {
          user: user && user.username ? user.username : (localStorage.getItem('username') || ''),
          text,
          first_name: user && (user.first_name || user.firstName) ? (user.first_name || user.firstName) : undefined,
          last_name: user && (user.last_name || user.lastName) ? (user.last_name || user.lastName) : undefined,
          avt: user && (user.avt || user.avatar) ? (user.avt || user.avatar) : undefined,
          createdAt: nowIso,
          created_at: nowIso
        };
        setPosts(prev => prev.map(p => p.id === id ? { ...p, comments: [...(p.comments || []), newComment] } : p));
        if (window.showToast) window.showToast('Gửi bình luận thất bại, đã lưu cục bộ', 'warning');
      }
    } else {
      const nowIso = (new Date()).toISOString();
      const newComment = {
        user: user && user.username ? user.username : (localStorage.getItem('username') || ''),
        text,
        first_name: user && (user.first_name || user.firstName) ? (user.first_name || user.firstName) : undefined,
        last_name: user && (user.last_name || user.lastName) ? (user.last_name || user.lastName) : undefined,
        avt: user && (user.avt || user.avatar) ? (user.avt || user.avatar) : undefined,
        createdAt: nowIso,
        created_at: nowIso
      };
      setPosts(prev => prev.map(p => p.id === id ? { ...p, comments: [...(p.comments || []), newComment] } : p));
    }
  }

  async function createPost(title, content) {
    const token = localStorage.getItem('token');
    // optional onProgress callback may be passed as second arg when title is FormData
    const onProgress = (typeof arguments[1] === 'function') ? arguments[1] : null;
    if (apiUrl && token) {
      try {
        // createPost can accept FormData when attachments are present. We'll expect caller to handle that.
        let body = JSON.stringify({ title, content });
        let headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
        if (title instanceof FormData) {
          // send via XHR to track upload progress if onProgress provided
          if (onProgress) {
            await new Promise((resolve, reject) => {
              const xhr = new XMLHttpRequest();
              xhr.open('POST', `${apiUrl}/api/posts`);
              xhr.setRequestHeader('Authorization', `Bearer ${token}`);
              xhr.upload.onprogress = function (e) { if (e.lengthComputable) { const p = Math.round((e.loaded / e.total) * 100); onProgress(p); } };
              xhr.onload = function () {
                if (xhr.status >= 200 && xhr.status < 300) { try { const data = JSON.parse(xhr.responseText); resolve(data); } catch (e) { resolve({}); } }
                else { reject(new Error('Create failed ' + xhr.status + ' ' + xhr.responseText)); }
              };
              xhr.onerror = function () { reject(new Error('Network error')); };
              xhr.send(title);
            });
            // after upload completes, fetch the created post from server to keep behavior consistent
            const res2 = await fetch(`${apiUrl}/api/posts`, { headers: { Authorization: `Bearer ${token}` } });
            if (!res2.ok) throw new Error('Fetch posts failed after upload');
            const data2 = await res2.json();
            // try to find latest post by author and title
            const found = (data2.posts || []).find(p => p.author === (user && user.username) && p.title === (title.get ? title.get('title') : ''));
            if (found) {
              // emulate previous response structure
              const data = { post: found };
              // reuse downstream logic by falling through
              title = data.post; content = data.post.content;
            } else {
              // fallback: continue without attachments in feed
              title = null;
            }
          } else {
            // no onProgress: fallback to fetch (browser will still upload)
            body = title; headers = { Authorization: `Bearer ${token}` };
            const res = await fetch(`${apiUrl}/api/posts`, { method: 'POST', headers, body });
            if (!res.ok) { const txt = await res.text(); throw new Error(`Create failed ${res.status} ${txt}`); }
            const data = await res.json();
            if (data && data.post) title = data.post; else title = null;
          }
        } else {
          const res = await fetch(`${apiUrl}/api/posts`, { method: 'POST', headers, body });
          if (!res.ok) { const txt = await res.text(); throw new Error(`Create failed ${res.status} ${txt}`); }
          const data = await res.json();
          if (data && data.post) title = data.post; else title = null;
        }
        // At this point, `title` may be the created post object (if upload path) or null; for JSON path we've already set title variable accordingly.
        let data = null;
        if (title && title.id) { data = { post: title }; }
        if (data && data.post) {
          const createdApproved = (typeof data.post.approved === 'boolean') ? data.post.approved : (data.post.approved === 1 || data.post.approved === '1');
          if (createdApproved) {
            const p = {
              id: data.post.id,
              author: data.post.author,
              author_first_name: data.post.author_first_name || (data.post.author && data.post.author.first_name) || null,
              author_last_name: data.post.author_last_name || (data.post.author && data.post.author.last_name) || null,
              authorSchoolId: data.post.authorSchoolId || data.post.author_school_id || null,
              title: data.post.title,
              content: data.post.content,
              createdAt: data.post.createdAt,
              likes: [],
              comments: [],
              approved: true,
              attachments: Array.isArray(data.post.attachments) ? data.post.attachments : (data.post.attachments ? JSON.parse(data.post.attachments) : [])
            };
            setPosts(prev => [p, ...prev]);
            if (window.showToast) window.showToast('Đã đăng bài', 'success');
            // ensure feed is fresh for non-teacher roles
            try { if (!(user && user.role === 'Teacher')) { await fetchPostsFromApi(); } } catch (e) { }
          }
          try {
            const res2 = await fetch(`${apiUrl}/api/posts/pending`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
            if (res2.ok) {
              const d2 = await res2.json();
              setPendingCount(Array.isArray(d2.posts) ? d2.posts.length : 0);
              if (pendingVisible) setPendingPosts(Array.isArray(d2.posts) ? d2.posts : []);
              // notify other tabs/components that pending changed
              try { signalPendingChange(); } catch (e) { }
            }
          } catch (e) { }
          // If current user is a Teacher, do not run the heavier refresh steps; we've updated pending count above.
          return;
        }
      } catch (e) { console.error('Create post error', e); setError('Không thể đăng lên server, dùng chế độ offline'); }
    }
    // fallback local: only insert a local placeholder when we are truly offline
    // (no apiUrl configured or no auth token). If API is configured and the
    // create failed, do NOT insert an empty placeholder — user will see the
    // error message instead and can retry.
    const tokenLocal = localStorage.getItem('token');
    if (!apiUrl || !tokenLocal) {
      const rawDate = new Date().toISOString();
      const fallbackApproved = user.role === 'Teacher' ? false : true;
      const next = { id: Date.now(), author: user.username, author_first_name: user && (user.first_name || user.firstName) || null, author_last_name: user && (user.last_name || user.lastName) || null, authorSchoolId: user.school_id || null, title, content, createdAt: rawDate, likes: [], comments: [], approved: fallbackApproved };
      setPosts(prev => [next, ...prev]);
      if (window.showToast) window.showToast('Đã đăng bài (offline)', 'success');
    } else {
      // API is configured and token exists; don't show a phantom post when create failed
      // error state has been set above (setError), so just return.
    }
  }

  // helper: ensure attachment urls point to api domain when path is relative
  function normalizeAttachmentUrl(a) {
    if (!a) return a;
    const copy = { ...a };
    const src = copy.url || copy.path || '';
    if (src && src.startsWith('/')) {
      // avoid double slashes
      const base = apiUrl ? apiUrl.replace(/\/$/, '') : '';
      copy.url = base + src;
    } else if (src && !src.match(/^https?:\/\//)) {
      // some stored paths may be 'uploads/name.jpg' (no leading slash)
      const base = apiUrl ? apiUrl.replace(/\/$/, '') : '';
      copy.url = base + '/' + src.replace(/^\//, '');
    } else {
      copy.url = src;
    }
    return copy;
  }

  // helper: normalize simple URL strings (for user avatars)
  function normalizeUserUrl(src) {
    if (!src) return src;
    const s = String(src || '');
    if (s.startsWith('http://') || s.startsWith('https://')) return s;
    // treat leading slash or relative path by prefixing apiUrl
    if (s.startsWith('/')) {
      const base = apiUrl ? apiUrl.replace(/\/$/, '') : '';
      return base + s;
    }
    // also handle paths like 'uploads/avt.jpg'
    if (!s.match(/^https?:\/\//)) {
      const base = apiUrl ? apiUrl.replace(/\/$/, '') : '';
      return base + '/' + s.replace(/^\//, '');
    }
    return s;
  }

  // Fetch posts from API and set state (callable from handlers to refresh feed)
  async function fetchPostsFromApi() {
    if (!apiUrl) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    setLoading(true); setError(null);
    try {
      const res = await fetch(`${apiUrl}/api/posts`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (data && Array.isArray(data.posts)) {
        const normalized = data.posts.map(p => ({
          id: p.id,
          author: p.author,
          author_first_name: p.author_first_name || (p.author && p.author.first_name) || null,
          author_last_name: p.author_last_name || (p.author && p.author.last_name) || null,
          author_display: p.author_display || p.author_full_name || p.author_fullname || p.full_name || (p.author && typeof p.author === 'object' && (p.author.full_name || p.author.fullName || p.author.name)) || null,
          authorRole: p.authorRole || p.role || '',
          authorSchoolId: p.authorSchoolId || p.author_school_id || null,
          title: p.title,
          content: p.content,
          createdAt: p.createdAt,
          likes: p.likes || [],
          comments: p.comments || [],
          approved: (typeof p.approved === 'boolean') ? p.approved : (p.approved === 1 || p.approved === '1'),
          attachments: (Array.isArray(p.attachments) ? p.attachments : (p.attachments ? (typeof p.attachments === 'string' ? JSON.parse(p.attachments) : p.attachments) : [])).map(a => normalizeAttachmentUrl(a))
        }));
        setPosts(normalized);
      }
    } catch (e) {
      console.error('fetchPostsFromApi failed', e);
    } finally {
      setLoading(false);
    }
  }

  async function editPost(id, title, content, opts = {}) {
    const token = localStorage.getItem('token');
    if (apiUrl && token) {
      try {
        // If opts is a FormData (file upload path), send it directly
        if (opts instanceof FormData) {
          // ensure auth
          opts.set && opts.set('AuthorizationSent', '1');
          const res = await fetch(`${apiUrl}/api/posts/${id}`, { method: 'PUT', headers: { Authorization: `Bearer ${token}` }, body: opts });
          if (!res.ok) throw new Error('update failed');
          const data = await res.json();
          if (data && data.post) updatePostInState(id, () => data.post);
          if (window.showToast) window.showToast('Cập nhật bài viết thành công', 'success');
          // For Teachers, reload page so changes are immediately reflected
          try { if (user && user.role === 'Teacher') { try { window.location.reload(); } catch (e) { } } } catch (e) { }
          return;
        }

        // Standard JSON path: include existingAttachments metadata if provided
        const payload = { title, content };
        if (opts && opts.existingAttachments) payload.attachments = opts.existingAttachments;
        const res = await fetch(`${apiUrl}/api/posts/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify(payload) });
        if (!res.ok) throw new Error('update failed');
        const data = await res.json();
        if (data && data.post) updatePostInState(id, () => data.post);
        if (window.showToast) window.showToast('Cập nhật bài viết thành công', 'success');
        // For Teachers, reload page so edits are immediately visible
        try { if (user && user.role === 'Teacher') { try { window.location.reload(); } catch (e) { } } } catch (e) { }
        return;
      } catch (e) { console.error('Edit post error', e); setError('Không thể cập nhật bài viết trên server'); }
    }
    // offline: update local
    updatePostInState(id, p => ({ ...p, title, content }));
    if (window.showToast) window.showToast('Cập nhật bài viết (offline)', 'success');
  }

  function signalPendingChange() {
    try { localStorage.setItem('wolad_pending_signal', String(Date.now())); } catch (e) { }
  }

  async function deletePost(id) {
    const token = localStorage.getItem('token');
    if (apiUrl && token) {
      try {
        const res = await fetch(`${apiUrl}/api/posts/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) throw new Error('delete failed');
        setPosts(prev => prev.filter(p => p.id !== id));
        if (window.showToast) window.showToast('Xóa bài viết thành công', 'success');
        return;
      } catch (e) { console.error('Delete post error', e); setError('Không thể xóa bài viết trên server'); }
    }
    setPosts(prev => prev.filter(p => p.id !== id));
    if (window.showToast) window.showToast('Xóa bài viết (offline)', 'success');
  }

  async function rejectPost(id) {
    const token = localStorage.getItem('token');
    if (apiUrl && token) {
      try {
        const res = await fetch(`${apiUrl}/api/posts/${id}/reject`, { method: 'PUT', headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) throw new Error('reject failed');
        updatePostInState(id, p => ({ ...p, approved: false }));
        if (window.showToast) window.showToast('Đã từ chối bài viết', 'success');
        // refresh pending/feed and notify other tabs
        try { await fetchPending(); await fetchPostsFromApi(); signalPendingChange(); } catch (e) { }
        return;
      } catch (e) { console.error('Reject failed', e); setError('Không thể từ chối bài viết trên server'); }
    }
    updatePostInState(id, p => ({ ...p, approved: false }));
    if (window.showToast) window.showToast('Đã từ chối bài viết (offline)', 'success');
    await fetchPending();
    await fetchPostsFromApi();
  }

  async function fetchPending() {
    if (!apiUrl) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const res = await fetch(`${apiUrl}/api/posts/pending`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('fetch pending failed');
      const data = await res.json();
      if (data && Array.isArray(data.posts)) {
        setPendingPosts(data.posts.map(p => {
          const rawAtts = Array.isArray(p.attachments) ? p.attachments : (p.attachments ? (typeof p.attachments === 'string' ? JSON.parse(p.attachments) : p.attachments) : []);
          return {
            id: p.id,
            author: p.author,
            author_first_name: p.author_first_name || (p.author && p.author.first_name) || null,
            author_last_name: p.author_last_name || (p.author && p.author.last_name) || null,
            authorRole: p.authorRole || p.role || '',
            authorSchoolId: p.authorSchoolId || p.author_school_id || null,
            title: p.title,
            content: p.content,
            createdAt: p.createdAt,
            likes: p.likes || [],
            comments: p.comments || [],
            approved: p.approved,
            attachments: rawAtts.map(a => normalizeAttachmentUrl(a))
          };
        }));
        setPendingCount(data.posts.length);
      }
    } catch (e) { console.error('Fetch pending failed', e); setError('Cannot fetch pending posts'); }
  }

  // Replace continuous polling with a one-shot visibility-triggered refresh.
  // When the tab becomes visible, we perform a single fetch appropriate to the role:
  // - If Teacher: refresh posts once
  // - If Mod and pending list is open: refresh pending once
  useEffect(() => {
    if (!apiUrl) return;
    function onVisibility() {
      if (typeof document === 'undefined') return;
      if (document.visibilityState !== 'visible') return;
      try {
        if (user && user.role === 'Teacher') {
          fetchPostsFromApi().catch(() => { });
        }
        if (user && user.role === 'Mod' && pendingVisible) {
          fetchPending().catch(() => { });
        }
      } catch (e) { }
    }
    document.addEventListener && document.addEventListener('visibilitychange', onVisibility);
    // also run once on mount if visible
    if (typeof document === 'undefined' || document.visibilityState === 'visible') onVisibility();
    return () => { document.removeEventListener && document.removeEventListener('visibilitychange', onVisibility); };
  }, [apiUrl, user && user.role, pendingVisible]);

  // Listen for storage events so other tabs/clients can notify us to refresh pending
  useEffect(() => {
    // debounce storage events to avoid rapid repeated fetches
    let storageTimer = null;
    function onStorage(e) {
      if (!e) return;
      if (e.key === 'wolad_pending_signal') {
        if (storageTimer) clearTimeout(storageTimer);
        storageTimer = setTimeout(() => { fetchPending().catch(() => { }); storageTimer = null; }, 300);
      }
    }
    window.addEventListener('storage', onStorage);
    return () => { window.removeEventListener('storage', onStorage); if (storageTimer) clearTimeout(storageTimer); };
  }, []);

  return (
    <div className="home-feed page-view">
      {/* Pending posts collapsed: click header to load list */}
      {(user.role === 'Teacher' || user.role === 'Mod') && (
        <div className="pending-section">
          <h3 className="pending-toggle" onClick={async () => { setPendingVisible(v => !v); if (!pendingVisible) { await fetchPending(); } }}>{pendingVisible ? '▼' : '▶'} Pending Posts {pendingCount > 0 && <span className="badge">{pendingCount}</span>}</h3>
          {pendingVisible && (
            <div className="pending-list">
              {pendingPosts.length === 0 && <div className="muted">No posts pending approval</div>}
              {pendingPosts.map(p => (
                <div key={p.id} className="pending-item" onClick={() => setPreviewPost(p)}>
                  <div className="pending-post-row">
                    <main className="pending-main">
                      <header>
                        {/* Show teacher as last_name + first_name when available, fallback to displayNameOf(p) */}
                        {(() => {
                          let teacherLabel = null;
                          if (p && (p.author_last_name || p.author_first_name)) {
                            teacherLabel = [p.author_last_name, p.author_first_name].filter(Boolean).join(' ');
                          } else if (p && p.author && typeof p.author === 'object') {
                            const ln = p.author.last_name || p.author.lastName || null;
                            const fn = p.author.first_name || p.author.firstName || null;
                            if (ln || fn) teacherLabel = [ln, fn].filter(Boolean).join(' ');
                            else teacherLabel = p.author.full_name || p.author.name || p.author.username || null;
                          } else {
                            teacherLabel = displayNameOf(p);
                          }
                          const className = (user && user.role === 'Mod' && p.author) ? (teacherClassMap[p.author] || null) : null;
                          return (
                            <div className="own-post-label">{teacherLabel}{className ? ` — Class ${className}` : ''} — {p.createdAt ? formatDateVN(p.createdAt) : ''}</div>
                          );
                        })()}
                        <span style={{ fontWeight: 500 }}>{p.title}</span>
                      </header>
                      <p className="content">{p.content}</p>
                      {p.attachments && p.attachments.length > 0 && (
                        <div className="attachment-grid">
                          {p.attachments.map((a, idx) => (
                            <div key={idx} className="att" onClick={(ev) => { ev.stopPropagation(); setLightboxItems(p.attachments); setLightboxIndex(idx); setLightboxOpen(true); }}>
                              {a.mime && a.mime.startsWith('image/') && <img src={a.url || a.path} alt={a.filename || ''} />}
                              {a.mime && a.mime.startsWith('video/') && <video src={a.url || a.path} />}
                              {(!a.mime || (!a.mime.startsWith('image/') && !a.mime.startsWith('video/'))) && <a href={a.url || a.path} target="_blank" rel="noreferrer">{a.filename || 'Tệp'}</a>}
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="post-actions">
                        {user.role === 'Teacher' && p.author === user.username && (
                          <>
                            <button onClick={(ev) => { ev.stopPropagation(); const norm = (p.attachments || []).map(a => normalizeAttachmentUrl(a)); setEditingPost({ ...p, attachments: norm }); setShowModal(true); }}>Edit</button>
                            <button onClick={(ev) => { ev.stopPropagation(); if (window.confirm('Are you sure you want to cancel this post?')) deletePost(p.id); }}>Cancel post</button>
                          </>
                        )}
                        {user.role === 'Mod' && (
                          <>
                            <button className="btn-modal approve" onClick={async (ev) => {
                              ev.stopPropagation(); try {
                                const token = localStorage.getItem('token'); const res = await fetch(`${apiUrl}/api/posts/${p.id}/approve`, { method: 'PUT', headers: { Authorization: `Bearer ${token}` } }); if (!res.ok) throw new Error('approve failed'); await res.json(); updatePostInState(p.id, x => ({ ...x, approved: true })); setPendingPosts(prev => prev.filter(pp => pp.id !== p.id)); try { await fetchPostById(p.id); await fetchPending(); await fetchPostsFromApi(); try { signalPendingChange(); } catch (e) { } } catch (e) { /* ignore */ } if (window.showToast) window.showToast('Approve successful', 'success'); // special-case: if current user is Teacher, reload page to show the approved post
                                if (user && user.role === 'Teacher') { try { window.location.reload(); } catch (e) { } }
                              } catch (e) { console.error(e); setError('Approve failed'); if (window.showToast) window.showToast('Approve failed', 'error'); }
                            }}>Approve</button>
                            <button className="btn-modal reject" onClick={(ev) => { ev.stopPropagation(); (async () => { if (!window.confirm('Are you sure you want to reject this post?')) return; try { await rejectPost(p.id); setPendingPosts(prev => prev.filter(pp => pp.id !== p.id)); try { signalPendingChange(); } catch (e) { } if (window.showToast) window.showToast('Post rejected', 'success'); } catch (e) { console.error(e); if (window.showToast) window.showToast('Reject failed', 'error'); } })(); }}>Reject</button>
                          </>
                        )}
                      </div>
                    </main>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {/* Mods are NOT allowed to create posts per requirement. Teachers/Admins/Company can create. */}
      {(user.role === 'Teacher') && (
        <>
          {/* inline composer box: clicking the text area opens the modal */}
          <div className="section-post-composer">
            <div className="inline-composer">
              <div className="inline-composer-left"><img src={normalizeUserUrl(user && user.avt) || (apiUrl ? apiUrl.replace(/\/$/, '') + '/uploads/avt/avt-def.jpg' : '/uploads/avt/avt-def.jpg')} alt="avatar" height={70} width={70} /></div>
              <div className="inline-composer-body" onClick={() => { setEditingPost(null); setModalInitialFiles([]); setShowModal(true); }}>
                <input readOnly placeholder="What’s new today?" />
                <div className="inline-composer-actions">
                  <label className="icon-btn-post" title="Upload Pictures">
                    <img src={picUpload} alt="Upload Pictures" height={24} width={24} />
                    <input type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={(e) => {
                      const incoming = Array.from(e.target.files || []);
                      if (incoming.length === 0) return;
                      // filter out videos that exceed client-side limit (videos only)
                      const filtered = incoming.filter(f => !(f && f.type && f.type.startsWith('video/') && f.size > MAX_ATTACHMENT_BYTES));
                      if (filtered.length !== incoming.length) {
                        const msg = `Một hoặc nhiều video vượt quá ${MAX_ATTACHMENT_MB}MB đã bị bỏ.`;
                        if (window.showToast) window.showToast(msg, 'error'); else alert(msg);
                      }
                      if (filtered.length === 0) { e.currentTarget.value = null; return; }
                      setModalInitialFiles(filtered);
                      setEditingPost(null);
                      setShowModal(true);
                      e.currentTarget.value = null;
                    }} />
                    Photos Upload
                  </label>
                  <label className="icon-btn-post" title="Upload Videos">
                    <img src={vidUpload} alt="Upload Videos" height={18} width={28} />
                    <input type="file" accept="video/*" multiple style={{ display: 'none' }} onChange={(e) => {
                      const incoming = Array.from(e.target.files || []);
                      if (incoming.length === 0) return;
                      const filtered = incoming.filter(f => !(f && f.type && f.type.startsWith('video/') && f.size > MAX_ATTACHMENT_BYTES));
                      if (filtered.length !== incoming.length) {
                        const msg = `Một hoặc nhiều video vượt quá ${MAX_ATTACHMENT_MB}MB đã bị bỏ.`;
                        if (window.showToast) window.showToast(msg, 'error'); else alert(msg);
                      }
                      if (filtered.length === 0) { e.currentTarget.value = null; return; }
                      setModalInitialFiles(filtered);
                      setEditingPost(null);
                      setShowModal(true);
                      e.currentTarget.value = null;
                    }} />
                    Videos Upload
                  </label>
                </div>
              </div>
            </div>

          </div>
          {/* <button className="new-post-btn" onClick={() => { setEditingPost(null); setModalInitialFiles([]); setShowModal(true); }}>Đăng bài mới</button> */}
          {showModal && (
            editingPost ? (
              <NewPostModal initialTitle={editingPost.title} initialContent={editingPost.content} initialAttachments={editingPost.attachments || []} isEdit={true} onClose={() => { setShowModal(false); setEditingPost(null); setModalInitialFiles([]); }} onCreate={async (title, content, opts) => { await editPost(editingPost.id, title, content, opts); setShowModal(false); setEditingPost(null); setModalInitialFiles([]); }} />
            ) : (
              <NewPostModal initialFiles={modalInitialFiles} onClose={() => { setShowModal(false); setModalInitialFiles([]); }} onCreate={async (title, content) => { await createPost(title, content); setShowModal(false); setModalInitialFiles([]); }} />
            )
          )}
        </>
      )}
      <div style={{ marginTop: 10, marginBottom: 10 }}>
        <h2 style={{ margin: 0 }}>{(user && user.role === 'Student' && isMobile) ? "Your teacher's content" : 'Previous posts by you'}</h2>
      </div>
      <div className="posts">
        {loading && <div className="muted">Loading posts...</div>}
        {(!loading && user && user.role === 'Student' && (!homeroomTeacherUsername || filteredVisiblePosts.length === 0)) ? (
          <div className="muted">No posts found from your Teacher.</div>
        ) : null}

        {filteredVisiblePosts.map(p => (
          <PostItem
            key={p.id}
            post={p}
            navigate={navigate}
            normalizeUserUrl={normalizeUserUrl}
            normalizeAttachmentUrl={normalizeAttachmentUrl}
            user={user}
            apiUrl={apiUrl}
            formatDateVN={formatDateVN}
            addComment={addComment}
            toggleLike={toggleLike}
            fetchPostById={fetchPostById}
            setLightboxItems={setLightboxItems}
            setLightboxIndex={setLightboxIndex}
            setLightboxOpen={setLightboxOpen}
            updatePostInState={updatePostInState}
            fetchPending={fetchPending}
            fetchPostsFromApi={fetchPostsFromApi}
            deletePost={deletePost}
            rejectPost={rejectPost}
            teacherClassMap={teacherClassMap}
          />
        ))}
      </div>
      {lightboxOpen && <Lightbox items={lightboxItems} startIndex={lightboxIndex} onClose={() => setLightboxOpen(false)} />}
      {previewPost && (
        <div className="modal-backdrop" onClick={() => setPreviewPost(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="own-post-label">{(previewPost && (previewPost.author_last_name || previewPost.author_first_name)) ? [previewPost.author_last_name, previewPost.author_first_name].filter(Boolean).join(' ') : displayNameOf(previewPost)}{(user && user.role === 'Mod' && previewPost && previewPost.author && previewPost.authorRole === 'Teacher' && teacherClassMap && teacherClassMap[previewPost.author]) ? ` — Class: ${teacherClassMap[previewPost.author]}` : ''} — {previewPost.createdAt ? formatDateVN(previewPost.createdAt) : ''}</div>
              <button onClick={() => setPreviewPost(null)}>✕</button>
            </div>
            <div className="modal-body">
              <h3>{previewPost.title}</h3>
              <p style={{ marginTop: 12 }}>{previewPost.content}</p>
              {previewPost.attachments && previewPost.attachments.length > 0 && (
                <div className="attachment-grid mt-8">
                  {previewPost.attachments.map((a, idx) => (
                    <div key={idx} className="att" onClick={(ev) => { ev.stopPropagation(); setLightboxItems(previewPost.attachments); setLightboxIndex(idx); setLightboxOpen(true); }}>
                      {a.mime && a.mime.startsWith('image/') && <img src={a.url || a.path} alt={a.filename || ''} />}
                      {a.mime && a.mime.startsWith('video/') && <video src={a.url || a.path} />}
                      {(!a.mime || (!a.mime.startsWith('image/') && !a.mime.startsWith('video/'))) && <a href={a.url || a.path} target="_blank" rel="noreferrer">{a.filename || 'Tệp'}</a>}
                    </div>
                  ))}
                </div>
              )}
              {/* comments removed for pending preview */}
            </div>
            <div className="modal-actions">
              {user.role === 'Mod' && (
                <>
                  <button className="btn-modal approve" onClick={async () => {
                    try {
                      const token = localStorage.getItem('token'); const res = await fetch(`${apiUrl}/api/posts/${previewPost.id}/approve`, { method: 'PUT', headers: { Authorization: `Bearer ${token}` } }); if (!res.ok) throw new Error('approve failed'); await res.json(); updatePostInState(previewPost.id, x => ({ ...x, approved: true })); setPendingPosts(prev => prev.filter(pp => pp.id !== previewPost.id)); try { await fetchPostById(previewPost.id); await fetchPending(); await fetchPostsFromApi(); } catch (e) { /* ignore */ } setPreviewPost(null); if (window.showToast) window.showToast('Approve successful', 'success'); // reload page for teachers so they immediately see the approved post
                      if (user && user.role === 'Teacher') { try { window.location.reload(); } catch (e) { } }
                    } catch (e) { console.error(e); setError('Approve failed'); if (window.showToast) window.showToast('Approve failed', 'error'); }
                  }}>Approve</button>
                  <button className="btn-modal reject" onClick={async () => { if (!window.confirm('Are you sure you want to reject this post?')) return; try { const token = localStorage.getItem('token'); const res = await fetch(`${apiUrl}/api/posts/${previewPost.id}/reject`, { method: 'PUT', headers: { Authorization: `Bearer ${token}` } }); if (!res.ok) throw new Error('reject failed'); updatePostInState(previewPost.id, p => ({ ...p, approved: false })); setPendingPosts(prev => prev.filter(pp => pp.id !== previewPost.id)); setPreviewPost(null); if (window.showToast) window.showToast('Post rejected', 'success'); } catch (e) { console.error(e); setError('Reject failed'); if (window.showToast) window.showToast('Reject failed', 'error'); } }}>Reject</button>
                </>
              )}
              <button className="btn-modal btn-muted" onClick={() => setPreviewPost(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PostComposer({ onCreate }) {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  return (
    <div className="composer">
      <input placeholder="Title" value={title} onChange={e => setTitle(e.target.value)} />
      <textarea placeholder="Content" value={content} onChange={e => setContent(e.target.value)} />
      <button onClick={() => { if (title && content) { onCreate(title, content); setTitle(''); setContent(''); } }}>Post</button>
    </div>
  );
}

function NewPostModal({ onClose, onCreate, initialFiles = [], initialAttachments = [], initialTitle, initialContent, isEdit }) {
  const [title, setTitle] = useState(initialTitle || '');
  const [content, setContent] = useState(initialContent || '');
  // files are new File objects selected in the UI
  const [files, setFiles] = useState([]);
  // existingAttachments are objects returned from server for already-uploaded attachments
  const [existingAttachments, setExistingAttachments] = useState(initialAttachments || []);
  const [previews, setPreviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [progress, setProgress] = useState(0);
  // remove a file by index
  const removeFile = (idx) => { setFiles(prev => prev.filter((_, i) => i !== idx)); };
  // remove an existing attachment by index
  const removeExistingAttachment = (idx) => { setExistingAttachments(prev => prev.filter((_, i) => i !== idx)); };
  const submit = async () => {
    if (!title || !content) return;
    setLoading(true);
    try {
      // if files attached, create FormData
      if ((files && files.length) || (isEdit && existingAttachments && existingAttachments.length)) {
        // When editing, send existingAttachments metadata so server preserves them.
        // If there are new files selected, use FormData to upload them together.
        if (files && files.length) {
          const fd = new FormData();
          fd.append('title', title);
          fd.append('content', content);
          // include metadata about existing attachments to keep (JSON string)
          fd.append('existingAttachments', JSON.stringify(existingAttachments));
          for (const f of files) fd.append('attachments', f);
          // pass a progress callback so uploader can report progress
          await onCreate(fd, (p) => { setProgress(p); });
        } else {
          // no new files, but we still want to send existingAttachments info for edits
          if (isEdit) {
            await onCreate(title, content, { existingAttachments });
          } else {
            await onCreate(title, content);
          }
        }
      } else {
        await onCreate(title, content);
      }
      setTitle(''); setContent('');
    } catch (e) { console.error(e); }
    setLoading(false);
  };
  // initialize files when parent passes `initialFiles` (e.g. from inline composer)
  useEffect(() => {
    if (initialFiles && initialFiles.length && files.length === 0) {
      // filter out oversized videos
      const filtered = (initialFiles || []).filter(f => !(f && f.type && f.type.startsWith('video/') && f.size > MAX_ATTACHMENT_BYTES));
      if (filtered.length !== (initialFiles || []).length) {
        const msg = `Một hoặc nhiều video vượt quá ${MAX_ATTACHMENT_MB}MB đã bị bỏ.`;
        if (window.showToast) window.showToast(msg, 'error'); else alert(msg);
      }
      setFiles(filtered);
    }
  }, [initialFiles]);

  // initialize existing attachments when editing
  useEffect(() => {
    if (initialAttachments && initialAttachments.length && existingAttachments.length === 0) {
      setExistingAttachments(initialAttachments);
    }
  }, [initialAttachments]);

  // generate previews for newly selected `files`
  useEffect(() => {
    const previewsArr = files.map(f => {
      try {
        if (f && f.type && f.type.startsWith('image/')) return { type: 'image', url: URL.createObjectURL(f), name: f.name };
        if (f && f.type && f.type.startsWith('video/')) return { type: 'video', url: URL.createObjectURL(f), name: f.name };
        return { type: 'file', url: null, name: f && f.name ? f.name : 'file' };
      } catch (e) {
        return { type: 'file', url: null, name: f && f.name ? f.name : 'file' };
      }
    });
    setPreviews(previewsArr);
    return () => {
      previewsArr.forEach(pp => { if (pp && pp.url) try { URL.revokeObjectURL(pp.url); } catch (e) { } });
    };
  }, [files]);
  return (
    <div className="modal-backdrop">
      <div className="modal">
        <h3>Create new Post</h3>
        <input placeholder="Title" value={title} onChange={e => setTitle(e.target.value)} />
        <textarea placeholder="Content" value={content} onChange={e => setContent(e.target.value)} />
        <div className={"attachments" + (dragOver ? ' drag-over' : '')} onDragOver={e => { e.preventDefault(); setDragOver(true); }} onDragLeave={e => { e.preventDefault(); setDragOver(false); }} onDrop={e => {
          e.preventDefault(); setDragOver(false);
          const incoming = Array.from(e.dataTransfer.files || []);
          if (incoming.length === 0) return;
          // filter out oversized videos
          const filteredIncoming = incoming.filter(f => !(f && f.type && f.type.startsWith('video/') && f.size > MAX_ATTACHMENT_BYTES));
          if (filteredIncoming.length !== incoming.length) {
            const msg = `Một hoặc nhiều video vượt quá ${MAX_ATTACHMENT_MB}MB đã bị bỏ.`;
            if (window.showToast) window.showToast(msg, 'error'); else alert(msg);
          }
          if (filteredIncoming.length === 0) return;
          setFiles(prev => { const map = {}; prev.forEach(f => { map[`${f.name}/${f.size}`] = f; }); filteredIncoming.forEach(f => { map[`${f.name}/${f.size}`] = f; }); return Object.values(map); });
        }}>
          <div className="drop-hint">Drag and drop images/videos here or</div>
          <label className="attachment-input">
            <input type="file" accept="image/*,video/*" multiple onChange={e => {
              const incoming = Array.from(e.target.files || []);
              if (incoming.length === 0) return;
              // filter out oversized videos
              const filteredIncoming = incoming.filter(f => !(f && f.type && f.type.startsWith('video/') && f.size > MAX_ATTACHMENT_BYTES));
              if (filteredIncoming.length !== incoming.length) {
                const msg = `Một hoặc nhiều video vượt quá ${MAX_ATTACHMENT_MB}MB đã bị bỏ.`;
                if (window.showToast) window.showToast(msg, 'error'); else alert(msg);
              }
              if (filteredIncoming.length === 0) { e.currentTarget.value = null; return; }
              setFiles(prev => {
                const map = {};
                prev.forEach(f => { map[`${f.name}/${f.size}`] = f; });
                filteredIncoming.forEach(f => { map[`${f.name}/${f.size}`] = f; });
                return Object.values(map);
              });
              e.currentTarget.value = null;
            }} />
            <div>Select picture/video</div>
            <div style={{fontSize:12}}><i>(*) Maximum 12 attachments, {MAX_ATTACHMENT_MB}MB per video</i></div>
          </label>
          <div className="previews">
            {/* show previews for newly selected files */}
            {previews.map((p, i) => (
              <div key={i} className="preview">
                {p.type === 'image' && <img src={p.url} alt={p.name} />}
                {p.type === 'video' && <video src={p.url} controls />}
                {p.type === 'file' && <div className="file-name">{p.name}</div>}
                <button className="remove" onClick={() => removeFile(i)}>✕</button>
              </div>
            ))}
            {/* show existing attachments (already uploaded) when editing */}
            {existingAttachments && existingAttachments.map((a, i) => (
              <div key={`ex-${i}`} className="preview existing">
                {a.mime && a.mime.startsWith('image/') && <img src={a.url || a.path} alt={a.filename || a.name || ''} />}
                {a.mime && a.mime.startsWith('video/') && <video src={a.url || a.path} controls />}
                {(!a.mime || (!a.mime.startsWith('image/') && !a.mime.startsWith('video/'))) && <div className="file-name">{a.filename || a.name || 'Tệp'}</div>}
                <button className="remove" onClick={() => removeExistingAttachment(i)}>✕</button>
              </div>
            ))}
          </div>
          {loading && files.length > 0 && (
            <div className="upload-progress"><div className="bar" style={{ width: progress + '%' }} /></div>
          )}
        </div>
        <div className="modal-actions">
          <button className="btn btn-primary" onClick={submit} disabled={loading}>{loading ? 'Loading...' : (isEdit ? 'Save' : 'Post')}</button>
          <button className="btn btn-danger" onClick={onClose} disabled={loading}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

function CommentBox({ post, onAdd }) {
  const [text, setText] = useState('');
  return (
    <div className="comment-box" onClick={(ev) => { ev.stopPropagation(); }}>
      <input placeholder="Write a comment..." value={text} onChange={e => setText(e.target.value)} />
      <button onClick={() => { if (text.trim()) { onAdd(text.trim()); setText(''); } }}>Comment</button>
    </div>
  );
}

// PostItem: encapsulates a single post render and comment-height logic
function PostItem({ post, navigate, normalizeUserUrl, normalizeAttachmentUrl, user, apiUrl, formatDateVN, addComment, toggleLike, fetchPostById, setLightboxItems, setLightboxIndex, setLightboxOpen, updatePostInState, fetchPending, fetchPostsFromApi, deletePost, rejectPost, teacherClassMap }) {
  const mainRef = useRef(null);
  const commentsRef = useRef(null);
  const contentRef = useRef(null);
  const [visibleCount, setVisibleCount] = useState(0); // number of comments currently visible
  const [initialLoaded, setInitialLoaded] = useState(false);
  const [contentClamped, setContentClamped] = useState(false);
  const [mainHeight, setMainHeight] = useState(0);

  useEffect(() => {
    const mainEl = mainRef.current;
    if (!mainEl) return;

    const observer = new ResizeObserver(entries => {
      for (let entry of entries) {
        // luôn cập nhật height thật của bài post
        setMainHeight(entry.contentRect.height);
      }
    });
    observer.observe(mainEl);

    return () => observer.disconnect();
  }, []);

  const isStudent = user && user.role === 'Student';
  const isMobile = typeof window !== 'undefined' ? window.innerWidth <= 520 : false;

  const attachments = Array.isArray(post.attachments) ? post.attachments : [];
  const visibleAtts = attachments.slice(0, 3);
  const extra = Math.max(0, attachments.length - 3);

  // measure how many comments fit into the post-main height
  // useLayoutEffect(() => {
  //   if (!mainRef.current) return;
  //   const mainHeight = mainRef.current.clientHeight;
  //   // create a temporary measurement div to measure comment heights
  //   const comments = (post.comments || []).slice(-50); // cap measurement
  //   if (!comments.length) { setVisibleCount(0); setInitialLoaded(true); return; }
  //   // approximate comment height by inserting measurement nodes
  //   const container = document.createElement('div');
  //   container.style.visibility = 'hidden';
  //   container.style.position = 'absolute';
  //   container.style.width = mainRef.current.clientWidth + 'px';
  //   container.style.pointerEvents = 'none';
  //   document.body.appendChild(container);
  //   let used = 0; let count = 0;
  //   for (let i = comments.length - 1; i >= 0; i--) {
  //     const c = comments[i];
  //     const d = document.createElement('div');
  //     d.className = 'comment';
  //     d.style.padding = '4px 0';
  //     d.style.fontSize = '14px';
  //     d.innerText = (c.user ? c.user + ': ' : '') + (c.text || '');
  //     container.appendChild(d);
  //     used += d.clientHeight || 20;
  //     if (used <= mainHeight * 1) { // allow comments to occupy up to ~100% of main column
  //       count++;
  //     } else break;
  //   }
  //   document.body.removeChild(container);
  //   // ensure at least 1 comment shows if any exist
  //   if (count <= 0 && comments.length) count = Math.min(1, comments.length);
  //   setVisibleCount(count);
  //   setInitialLoaded(true);
  // }, [post, mainRef.current]);

  // determine whether post content overflows two lines on mobile
  useLayoutEffect(() => {
    try {
      if (!contentRef.current) { setContentClamped(false); return; }
      if (typeof window === 'undefined') { setContentClamped(false); return; }
      const isMobileNow = window.innerWidth <= 520;
      if (!isMobileNow) { setContentClamped(false); return; }
      const el = contentRef.current;
      const cs = window.getComputedStyle(el);
      const lineHeight = parseFloat(cs.lineHeight) || 18;
      const maxH = lineHeight * 2 + 0.5;
      // compare scrollHeight to max height
      const needsClamp = el.scrollHeight > maxH + 1;
      setContentClamped(needsClamp);
    } catch (e) { setContentClamped(false); }
  }, [post.content]);

  // show more increments by 10, maximum to all comments
  function onSeeMore() {
    const total = (post.comments || []).length;
    const next = Math.min(total, visibleCount + 10 || 10);
    setVisibleCount(next);
    // after user asked to see more, make the comment list scrollable (CSS handles overflow)
    if (commentsRef.current) commentsRef.current.style.overflowY = 'auto';
  }

  // initial comments slice (show most recent visibleCount comments)
  const commentsToRender = (() => {
    const all = post.comments || [];
    if (!all.length) return [];
    // On mobile for Students show at most 2 newest comments and provide a See more
    if (isStudent && isMobile) {
      const last = all.slice(-2);
      return last.slice().reverse();
    }
    const last = all.slice(-visibleCount || -10);
    return last.slice().reverse();
  })();

  // compute aside style: for student on mobile do not pin height to mainHeight
  const asideStyle = (isStudent && isMobile) ? { height: 'auto' } : { height: mainHeight + 12 ? `${mainHeight + 12}px` : 'auto' };
  const asideStyle2 = (isStudent && isMobile) ? { height: 'auto' } : { height: mainHeight + 40 ? `${mainHeight + 40}px` : 'auto' };

  return (
    <>
      <article
        key={post.id}
        className="post"
        onClick={(e) => {
          try {
            if (e.defaultPrevented) return;
            const tgt = e.target;
            if (tgt && typeof tgt.closest === 'function') {
              const interactive = tgt.closest('button, a, input, textarea, label');
              if (interactive) return;
            } else {
              const tag = tgt && tgt.tagName;
              if (tag === 'BUTTON' || tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'A' || tag === 'LABEL') return;
            }
            navigate(`/posts/${post.id}`);
          } catch (err) {
            try { navigate(`/posts/${post.id}`); } catch (e) { }
          }
        }}
      >
        <div className="post-row">
          <main className="post-main" ref={mainRef}>
            {/* For Students on small screens we render a different header layout so the date sits to the right of the teacher's name */}
            <header className={"post-header" + (isStudent && isMobile ? ' student-mobile-header' : '')}>
              <div className="post-author">
                <div className="post-avt"><img src={normalizeUserUrl(post && post.author && post.author.avt) || (apiUrl ? apiUrl.replace(/\/$/, '') + '/uploads/avt/avt-def.jpg' : '/uploads/avt/avt-def.jpg')} alt="avatar" height={50} width={50} /></div>
                <div className="post-author-meta">
                  <h4 className="post-author-name">{(post.author_last_name || post.author_first_name) ? [post.author_last_name, post.author_first_name].filter(Boolean).join(' ') : (typeof post.author === 'string' ? post.author : displayNameOf(post))}{(user && user.role === 'Mod' && post && post.author && post.authorRole === 'Teacher' && teacherClassMap && teacherClassMap[post.author]) ? <span style={{ fontWeight: 'normal', marginLeft: 8, fontSize: '0.9rem', color: '#666' }}>Class {teacherClassMap[post.author]}</span> : ""}</h4>
                  <div className="muted post-author-role">{post.authorRole || ''}</div>
                </div>
              </div>
              <div className="post-date muted">{formatDateVN(post.createdAt)}</div>
            </header>
            <hr className="line-post" />
            <div className="post-ct-detail">
              <h3 className="post-title">{post.title}</h3>
              <div ref={contentRef} className={"content" + (contentClamped ? ' clamp-2' : '')}>{post.content}</div>
              {contentClamped && (
                <div>
                  <button className="see-more-btn" onClick={(ev) => { ev.stopPropagation(); navigate(`/posts/${post.id}`); }}>See more</button>
                </div>
              )}
            </div>
            {attachments.length > 0 && (
              <div className={`attachment-grid ${(attachments.length === 1 ? 'single' : (attachments.length === 2 ? 'two' : 'three'))}`}>
                {(() => {
                  // Mobile student: exact layout rules requested by designer/user
                  if (isStudent && isMobile) {
                    const total = attachments.length;
                    // 1 image: full-width horizontal
                    if (total === 1) {
                      const a = attachments[0];
                      return (
                        <div className="student-mobile-layout">
                          <div className="att att-top" onClick={(ev) => { ev.stopPropagation(); setLightboxItems(attachments); setLightboxIndex(0); setLightboxOpen(true); }}>
                            {a.mime && a.mime.startsWith('image/') && <img src={a.url || a.path} alt={a.filename || ''} />}
                            {a.mime && a.mime.startsWith('video/') && <video src={a.url || a.path} />}
                          </div>
                        </div>
                      );
                    }

                    // 2 images: both on same row, each 50% width
                    if (total === 2) {
                      const a = attachments[0];
                      const b = attachments[1];
                      return (
                        <div className="student-mobile-layout">
                          <div className="att-grid-bottom two-cols">
                            <div className="att att-small" onClick={(ev) => { ev.stopPropagation(); setLightboxItems(attachments); setLightboxIndex(0); setLightboxOpen(true); }}>
                              {a.mime && a.mime.startsWith('image/') && <img src={a.url || a.path} alt={a.filename || ''} />}
                              {a.mime && a.mime.startsWith('video/') && <video src={a.url || a.path} />}
                            </div>
                            <div className="att att-small" onClick={(ev) => { ev.stopPropagation(); setLightboxItems(attachments); setLightboxIndex(1); setLightboxOpen(true); }}>
                              {b.mime && b.mime.startsWith('image/') && <img src={b.url || b.path} alt={b.filename || ''} />}
                              {b.mime && b.mime.startsWith('video/') && <video src={b.url || b.path} />}
                            </div>
                          </div>
                        </div>
                      );
                    }

                    // 3 images: first full-width on top, two below side-by-side
                    if (total === 3) {
                      const top = attachments[0];
                      const left = attachments[1];
                      const right = attachments[2];
                      return (
                        <div className="student-mobile-layout">
                          <div className="att att-top" onClick={(ev) => { ev.stopPropagation(); setLightboxItems(attachments); setLightboxIndex(0); setLightboxOpen(true); }}>
                            {top.mime && top.mime.startsWith('image/') && <img src={top.url || top.path} alt={top.filename || ''} />}
                            {top.mime && top.mime.startsWith('video/') && <video src={top.url || top.path} />}
                          </div>
                          <div className="att-grid-bottom">
                            <div className="att att-small" onClick={(ev) => { ev.stopPropagation(); setLightboxItems(attachments); setLightboxIndex(1); setLightboxOpen(true); }}>
                              {left.mime && left.mime.startsWith('image/') && <img src={left.url || left.path} alt={left.filename || ''} />}
                              {left.mime && left.mime.startsWith('video/') && <video src={left.url || left.path} />}
                            </div>
                            <div className="att att-small" onClick={(ev) => { ev.stopPropagation(); setLightboxItems(attachments); setLightboxIndex(2); setLightboxOpen(true); }}>
                              {right.mime && right.mime.startsWith('image/') && <img src={right.url || right.path} alt={right.filename || ''} />}
                              {right.mime && right.mime.startsWith('video/') && <video src={right.url || right.path} />}
                            </div>
                          </div>
                        </div>
                      );
                    }

                    // >=4: use 3-image visual (1 top + 2 bottom), but bottom-right shows overlay +remaining
                    const top = attachments[0];
                    const left = attachments[1];
                    const right = attachments[2];
                    const remaining = Math.max(0, attachments.length - 3);
                    return (
                      <div className="student-mobile-layout">
                        <div className="att att-top" onClick={(ev) => { ev.stopPropagation(); setLightboxItems(attachments); setLightboxIndex(0); setLightboxOpen(true); }}>
                          {top.mime && top.mime.startsWith('image/') && <img src={top.url || top.path} alt={top.filename || ''} />}
                          {top.mime && top.mime.startsWith('video/') && <video src={top.url || top.path} />}
                        </div>
                        <div className="att-grid-bottom">
                          <div className="att att-small" onClick={(ev) => { ev.stopPropagation(); setLightboxItems(attachments); setLightboxIndex(1); setLightboxOpen(true); }}>
                            {left.mime && left.mime.startsWith('image/') && <img src={left.url || left.path} alt={left.filename || ''} />}
                            {left.mime && left.mime.startsWith('video/') && <video src={left.url || left.path} />}
                          </div>
                          <div className="att att-small att-overlay" onClick={(ev) => { ev.stopPropagation(); setLightboxItems(attachments); setLightboxIndex(2); setLightboxOpen(true); }}>
                            {right.mime && right.mime.startsWith('image/') && <img src={right.url || right.path} alt={right.filename || ''} />}
                            {right.mime && right.mime.startsWith('video/') && <video src={right.url || right.path} />}
                            {remaining > 0 && <div className="overlay-count">+{remaining}</div>}
                          </div>
                        </div>
                      </div>
                    );
                  }

                  // Default: desktop/tablet or non-student roles — up to 3 thumbnails
                  return visibleAtts.map((a, idx) => {
                    const isLastVisible = idx === visibleAtts.length - 1;
                    const remaining = Math.max(0, attachments.length - visibleAtts.length);
                    const showOverlay = remaining > 0 && isLastVisible;
                    return (
                      <div
                        key={idx}
                        className={`att${showOverlay ? ' att-overlay more' : ''}`}
                        onClick={(ev) => { ev.stopPropagation(); setLightboxItems(attachments); setLightboxIndex(idx); setLightboxOpen(true); }}
                      >
                        {a.mime && a.mime.startsWith('image/') && <img src={a.url || a.path} alt={a.filename || ''} />}
                        {a.mime && a.mime.startsWith('video/') && <video src={a.url || a.path} />}
                        {showOverlay && <div className="overlay-count">+{remaining}</div>}
                      </div>
                    );
                  });
                })()}
              </div>
            )}
            <div className="post-actions">
              <button onClick={(ev) => { ev.stopPropagation(); toggleLike(post.id); }} className={(post.likes || []).includes(user.username) ? 'liked' : ''}>Like ({(post.likes || []).length})</button>
              {user.role === 'Mod' && post.authorRole === 'Teacher' && post.approved === false && user.school_id && post.authorSchoolId && String(user.school_id) === String(post.authorSchoolId) && (
                <button onClick={async (ev) => { ev.stopPropagation(); try { const token = localStorage.getItem('token'); const res = await fetch(`${apiUrl}/api/posts/${post.id}/approve`, { method: 'PUT', headers: { Authorization: `Bearer ${token}` } }); if (!res.ok) throw new Error('approve failed'); await res.json(); updatePostInState(post.id, x => ({ ...x, approved: true })); try { await fetchPostById(post.id); await fetchPending(); await fetchPostsFromApi(); } catch (e) { } if (window.showToast) window.showToast('Approve successful', 'success'); if (user && user.role === 'Teacher') { try { window.location.reload(); } catch (e) { } } } catch (e) { console.error('Approve failed', e); if (window.showToast) window.showToast('Approve failed', 'error'); } }}>Approve</button>
              )}
            </div>
          </main>
          <aside className={"post-comments" + (isStudent ? ' student-small' : '')} style={asideStyle2} onClick={(ev) => { ev.stopPropagation(); }}>
            <div className="comment-panel-h-act">
              <span>Comment</span>
              <button className="see-more-btn" onClick={(ev) => { ev.stopPropagation(); navigate(`/posts/${post.id}`); }}>See more</button>
            </div>
            <div className="comment-box-panel" style={asideStyle}>
              <div
                className="comment-list"
                ref={commentsRef}
                onClick={(ev) => { ev.stopPropagation(); }}
                style={{
                  height: "100%",
                  overflowY: "auto"
                }}
              >
                {(post.comments || []).length === 0 && <div className="muted">No comment</div>}
                {commentsToRender.length === 0 && (post.comments || []).length > 0 && (
                  <div className="muted">No comment (hidden)</div>
                )}
                {commentsToRender.map((c, i) => {
                  // prefer last_name + first_name
                  const last = c.last_name || c.lastName || c.lastname || c.family_name || '';
                  const first = c.first_name || c.firstName || c.firstname || c.given_name || '';
                  // Strictly assemble name from last_name + first_name (per UX requirement).
                  // If both are missing, fall back only to the raw username string.
                  let commenterName = '';
                  if (last || first) commenterName = [last, first].filter(Boolean).join(' ');
                  else commenterName = (typeof c.user === 'string') ? c.user : (c.user && c.user.username) ? c.user.username : '';

                  // avatar: prefer explicit `avt` field (may be absolute or relative URL)
                  const rawAvt = c.avt || c.user_avt || c.avatar || (c.user && typeof c.user === 'object' && (c.user.avt || c.user.avatar)) || null;
                  const avatarSrc = normalizeUserUrl(rawAvt) || (apiUrl ? apiUrl.replace(/\/$/, '') + '/uploads/avt/avt-def.jpg' : '/uploads/avt/avt-def.jpg');

                  return (
                    <div key={i} className="comment comment-row" onClick={(ev) => { ev.stopPropagation(); }}>
                      <img className="comment-avt" src={avatarSrc} alt={commenterName} width={32} height={32} />
                      <div className="comment-body">
                        <div className="comment-header">
                          <b>{commenterName || (typeof c.user === 'string' ? c.user : 'User')}</b>
                          {(c && (c.createdAt || c.created_at)) ? <span className="comment-time">{formatDateVN(c.createdAt || c.created_at)}</span> : null}
                        </div>
                        <div>{c.text}</div>
                      </div>
                    </div>
                  );
                })}
                {/* {((post.comments || []).length > commentsToRender.length) && (
      <div style={{ marginTop: 8 }}>
        <button className="see-more-btn" onClick={(ev) => { ev.stopPropagation(); navigate(`/posts/${post.id}`); }}>See more...</button>
      </div>
    )} */}
              </div>

              <div className="comment-box-side">
                <CommentBox post={post} onAdd={(text) => { addComment(post.id, text); }} />
              </div>
            </div>
          </aside>
        </div>
      </article></>
  );
}
